import { useEffect, useRef, useState } from 'react';

/** How long the answered question takes to leave before the next one arrives. */
const EXIT_MS = 140;

export type FlowDirection = 'forward' | 'back';

function prefersMotion(): boolean {
  // jsdom and old browsers have no matchMedia: no motion, no delay.
  return window.matchMedia?.('(prefers-reduced-motion: no-preference)').matches ?? false;
}

/**
 * Which question is on screen, and the short exit that plays before the next
 * one mounts. Reduced motion (and tests) swap instantly.
 */
export function useStepFlow(total: number) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<FlowDirection>('forward');
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function goTo(next: number, nextDirection: FlowDirection) {
    if (leaving || next < 0 || next >= total || next === index) return;
    setDirection(nextDirection);
    // Keys pressed while the answered question leaves must never edit it.
    (document.activeElement as HTMLElement | null)?.blur?.();
    if (!prefersMotion()) {
      setIndex(next);
      return;
    }
    setLeaving(true);
    timer.current = window.setTimeout(() => {
      setIndex(next);
      setLeaving(false);
    }, EXIT_MS);
  }

  return {
    index: Math.min(index, total - 1),
    direction,
    leaving,
    next: () => goTo(index + 1, 'forward'),
    back: () => goTo(index - 1, 'back'),
    jump: (to: number) => goTo(to, to > index ? 'forward' : 'back'),
  };
}
